import { isFiatMarketCode } from "@/utils/fiatMarket";
import { isStableCoin } from "@/components/Pages/Convert/convertV2";
import { resolveAllowedFiatQuotesForRecommend } from "@/components/Pages/Market/MarketList/cfdTabListSource";

/** v2 convertType：1/4/5/6 涉及「其它加密货币」 */
const SPOT_CONVERT_V2_TYPES = new Set([1, 4, 5, 6]);
const SPOT_STABLE_QUOTES = new Set(["USDT", "USDC"]);

function normalizeCode(code) {
  return String(code || "").toUpperCase();
}

function toDisplaySymbol(base, quote) {
  const b = String(base || "").toLowerCase();
  const q = String(quote || "").toLowerCase();
  if (!b || !q || b === q) return "";
  return `${b}_${q}`;
}

function toCfdSymbol(displaySymbol) {
  const base = String(displaySymbol || "").replace(/_cfd$/i, "");
  return base ? `${base}_cfd` : "";
}

function isDelisted(cfg) {
  return cfg && String(cfg.state || "").toUpperCase() === "DELISTED";
}

function isDisplayLevelVisible(cfg) {
  return String(cfg?.displayLevel || "").toUpperCase() !== "NONE";
}

/** 「其它加密货币」：非法币、非 USDT/USDC 稳定币 */
function isOtherCrypto(code) {
  const upper = normalizeCode(code);
  if (!upper) return false;
  return !isFiatMarketCode(upper) && !isStableCoin(upper);
}

function isSpotQuote(code) {
  const upper = normalizeCode(code);
  return isFiatMarketCode(upper) || isStableCoin(upper);
}

function addSpotPair(pairSet, crypto, quote) {
  const c = normalizeCode(crypto);
  const q = normalizeCode(quote);
  if (!isOtherCrypto(c) || !isSpotQuote(q) || c === q) return;
  const sym = toDisplaySymbol(c, q);
  if (sym) pairSet.add(sym);
}

/** 从 marketConfig 取展示 symbol 对应配置（兼容仅存在 *_cfd 的情况） */
export function getSpotMarketConfig(displaySymbol, marketConfig = {}) {
  if (!displaySymbol) return null;
  return marketConfig[displaySymbol] || marketConfig[toCfdSymbol(displaySymbol)] || null;
}

/** 展示 symbol 是否在市场配置中、未下架且 displayLevel 可展示 */
export function isSpotSymbolAvailable(displaySymbol, marketConfig = {}) {
  const cfg = getSpotMarketConfig(displaySymbol, marketConfig);
  if (!cfg) return false;
  if (isDelisted(cfg)) return false;
  return isDisplayLevelVisible(cfg);
}

/**
 * 从 convert/config/list 推导 Spot 交易对（不带 _cfd）。
 * v2 字段约定：currencyCode = from，cryptoCurrencyCode = to。
 *
 * | type | 方向           | Spot 展示对     |
 * |------|----------------|-----------------|
 * | 1    | 法币 → 其它加密 | {crypto}_{fiat} |
 * | 4    | 稳定 → 其它加密 | {crypto}_usdt   |
 * | 5    | 其它加密 → 法币 | {crypto}_{fiat} |
 * | 6    | 其它加密 → 稳定 | {crypto}_usdt   |
 *
 * legacy type=2 SELL（currencyCode=法币, cryptoCurrencyCode=卖出币）→ {crypto}_{fiat}
 */
export function extractSpotPairsFromConvertConfig(configs = []) {
  const pairs = new Set();

  (configs || [])
    .filter((c) => c && c.enabled !== 0)
    .forEach((c) => {
      const type = Number(c.type);
      const from = normalizeCode(c.currencyCode);
      const to = normalizeCode(c.cryptoCurrencyCode);

      if (SPOT_CONVERT_V2_TYPES.has(type)) {
        if (type === 1 && isFiatMarketCode(from) && isOtherCrypto(to)) {
          addSpotPair(pairs, to, from);
        } else if (type === 4 && isStableCoin(from) && isOtherCrypto(to)) {
          addSpotPair(pairs, to, from);
        } else if (type === 5 && isOtherCrypto(from) && isFiatMarketCode(to)) {
          addSpotPair(pairs, from, to);
        } else if (type === 6 && isOtherCrypto(from) && isStableCoin(to)) {
          addSpotPair(pairs, from, to);
        }
        return;
      }

      // legacy type=1 BUY：法币 → 其它加密
      if (type === 1 && isFiatMarketCode(from) && isOtherCrypto(to)) {
        addSpotPair(pairs, to, from);
        return;
      }

      // legacy type=2 SELL：其它加密 → 法币（与 v2 type=2 法币→USDT 区分：to 为非稳定加密）
      if (type === 2 && isFiatMarketCode(from) && isOtherCrypto(to)) {
        addSpotPair(pairs, to, from);
      }
    });

  return pairs;
}

/** 从展示 symbol 解析 quote（如 bnb_brl → BRL） */
export function getSpotQuoteUpper(displaySymbol) {
  const parts = String(displaySymbol || "").toLowerCase().split("_").filter(Boolean);
  if (parts.length < 2) return "";
  return parts[parts.length - 1].toUpperCase();
}

/** Spot 二级 Tab：usdt | usdc | fiat */
export function getSpotQuoteSubTab(displaySymbol) {
  const quote = getSpotQuoteUpper(displaySymbol);
  if (quote === "USDT") return "usdt";
  if (quote === "USDC") return "usdc";
  if (isFiatMarketCode(quote)) return "fiat";
  return "";
}

export function spotSymbolMatchesSubTab(displaySymbol, spotSubTab) {
  if (!spotSubTab) return true;
  return getSpotQuoteSubTab(displaySymbol) === spotSubTab;
}

export function compareSpotByPriceDesc(a, b) {
  const priceA = Number(a?.c) || 0;
  const priceB = Number(b?.c) || 0;
  if (priceB !== priceA) return priceB - priceA;
  return String(a?.s || "").localeCompare(String(b?.s || ""));
}

/** 与 Fiat-M / Favourite 推荐一致的用户法币白名单 */
export function resolveSpotAllowedFiatQuotes(activeFiatCodes, activeFiatCode) {
  return resolveAllowedFiatQuotesForRecommend(activeFiatCodes, activeFiatCode);
}

/** 法币 quote 是否在用户允许列表内；USDT / USDC 始终展示 */
export function isSpotQuoteAllowed(quote, activeFiatCodes, activeFiatCode) {
  const upper = normalizeCode(quote);
  if (!upper) return false;
  if (SPOT_STABLE_QUOTES.has(upper)) return true;
  const allowed = resolveSpotAllowedFiatQuotes(activeFiatCodes, activeFiatCode);
  return allowed.includes(upper);
}

export function filterSpotDisplaySymbolsByQuote(symbols, activeFiatCodes, activeFiatCode) {
  return (symbols || []).filter((sym) =>
    isSpotQuoteAllowed(getSpotQuoteUpper(sym), activeFiatCodes, activeFiatCode)
  );
}

function spotQuoteSortTier(quote, fiatOrderMap) {
  const upper = normalizeCode(quote);
  if (upper === "USDT") return 0;
  if (upper === "USDC") return 1;
  if (fiatOrderMap.has(upper)) return 2 + fiatOrderMap.get(upper);
  return 999;
}

/** Spot 默认排序：USDT → USDC → 用户法币（法币顺序与 Fiat-M 一致），同档内 displayWeight 降序 */
export function compareSpotDisplaySymbols(a, b, marketConfig = {}, activeFiatCodes, activeFiatCode) {
  const symA = String(a || "");
  const symB = String(b || "");
  const allowedFiats = resolveSpotAllowedFiatQuotes(activeFiatCodes, activeFiatCode);
  const fiatOrderMap = new Map(allowedFiats.map((c, i) => [normalizeCode(c), i]));

  const qa = getSpotQuoteUpper(symA);
  const qb = getSpotQuoteUpper(symB);
  const tierDiff = spotQuoteSortTier(qa, fiatOrderMap) - spotQuoteSortTier(qb, fiatOrderMap);
  if (tierDiff !== 0) return tierDiff;

  const weight = (sym) => {
    const cfg = getSpotMarketConfig(sym, marketConfig);
    return cfg ? parseInt(cfg.displayWeight, 10) || 0 : 0;
  };
  const weightDiff = weight(symB) - weight(symA);
  if (weightDiff !== 0) return weightDiff;

  return symA.localeCompare(symB);
}

export function sortSpotDisplaySymbols(symbols, marketConfig = {}, activeFiatCodes, activeFiatCode) {
  return [...(symbols || [])].sort((a, b) =>
    compareSpotDisplaySymbols(a, b, marketConfig, activeFiatCodes, activeFiatCode)
  );
}

/**
 * 解析首页 Spot 展示交易对：按 convert 配置拼对，校验 *_cfd 行情，
 * 法币对按用户法币白名单过滤，并按 USDT → USDC → 法币 排序。
 */
export function resolveSpotDisplaySymbolsFromConvertConfig(
  configs = [],
  marketConfig = {},
  options = {}
) {
  const { activeFiatCodes, activeFiatCode } = options;
  const pairs = extractSpotPairsFromConvertConfig(configs);
  const symbols = [];
  const seen = new Set();

  pairs.forEach((displaySymbol) => {
    if (seen.has(displaySymbol)) return;
    if (!isSpotSymbolAvailable(displaySymbol, marketConfig)) return;
    seen.add(displaySymbol);
    symbols.push(displaySymbol);
  });

  const filtered = filterSpotDisplaySymbolsByQuote(symbols, activeFiatCodes, activeFiatCode);
  return sortSpotDisplaySymbols(filtered, marketConfig, activeFiatCodes, activeFiatCode);
}

/** 在 24h ticker 列表中查找 Spot 展示 symbol 的行情（优先 *_cfd） */
export function findSpotTickerRecord(displaySymbol, tickers = []) {
  const cfdSymbol = toCfdSymbol(displaySymbol);
  const found = (tickers || []).find((obj) => obj?.s === displaySymbol || obj?.s === cfdSymbol);
  if (!found) return { s: displaySymbol };
  return found.s === cfdSymbol ? { ...found, s: displaySymbol } : found;
}
